import _ from 'lodash';
import { set } from 'vue-demi';
import { defineStore } from 'pinia';

import { IAgent, IAgentMultiUser, AgentStatus, AgentStatusSettings, RuleGroup } from '@/types';
import { AgentStatusResponse } from './types';
import { AGENT_STATUS, AGENT_STATUS_API, USER_ROLE, STATUS_CHANGE_REASON } from '@/constants';

export const useAgentsStore = defineStore('HITL-Agents', {
  state: () => {
    return {
      /**
       * Agent info for current user is saved separately,
       * and is not available in the general list.
       */
      agent: undefined as IAgent | undefined,
      agentMultiUser: undefined as IAgentMultiUser | undefined,
      agentsById: {} as { [agentId: string]: IAgent; },
      settings: {} as AgentStatusSettings,
      /** Status responses for active agent with timestamp earlier than this will be ignored */
      statusUpdateTimestamp: 0,
      isStatusLoading: false,
      /** Changing from "true" to "false" acts as a trigger for "Ping Status" feature */
      isStatusInitializing: false,
      isAgentListLoading: false,
      isActiveAgentReady: false,
      /** Agents can be requested individually */
      isLoadingAgentById: {} as { [agentId: string]: boolean; },
      /** Indicator of a state when client status doesn't match status on server */
      isStatusDesync: false,
    };
  },
  getters: {
    /** In most cases we don't include current agent when displaying list of agents */
    agents: state => Object.values(state.agentsById),
    /** List of agents with active user */
    agentsWithUser: state => {
      return state.agent
        ? { [state.agent!.id]: state.agent, ...state.agentsById }
        : state.agentsById;
    },
    activeAgentStatusData: state => state.agent?.statusData as AgentStatus & { [key: string]: string; },
    activeAgentStatusKey(): string {
      return this.activeAgentStatusData?.key || '';
    },

    activeAgentStatus(state): AGENT_STATUS_API {
      return state.agent?.statusData.status as AGENT_STATUS_API
        || _.get(this.settings.statuses, [this.activeAgentStatusKey, 'status'])
        || AGENT_STATUS_API.OFFLINE;
    },
    /** TODO: Busy reason is a legacy property */
    activeAgentStatusLabel(): string {
      return this.activeAgentStatusData?.label || this.activeAgentStatusData?.busyReason || '';
    },
    activeAgentUIStatus(state): AGENT_STATUS {
      const status = state.isStatusDesync
        ? AGENT_STATUS.DESYNC
        : this.activeAgentStatus;
      return status as AGENT_STATUS;
    },

    statusOptions: state => Object.values(state.settings?.statuses || {}),
    activeAgentStatusOption(): AgentStatus | undefined {
      return this.statusOptions.find(option => {
        const { key, status, label = '' } = option;
        if (this.activeAgentStatusKey === key) {
          return true;
        }

        /* Legacy status matching */
        return status === this.activeAgentStatus && label === this.activeAgentStatusLabel;
      });
    },

    getAgentById: state => {
      return (agentId: string): IAgent | undefined => {
        return agentId === state.agent?.id
          ? state.agent
          : state.agentsById[agentId];
      };
    },
    isUserAnAgent: state => !!state.agent?.ruleGroupIds.length,
    isUserAnAdmin: state => {
      const ADMIN_ROLES = [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN];
      return ADMIN_ROLES.includes(state.agent?.role);
    },
  },
});

export const useAgentsSetters = () => {
  const store = useAgentsStore();

  const setActiveAgent = (agent: IAgent) => {
    /* In some cases agent list can be ready even before active agent is set */
    const agentsById = store.agentsById;
    delete agentsById[agent.id];

    store.agent = agent;
    store.agentsById = agentsById;
  };

  const setAgentStatus = (args: {
    status: AgentStatusResponse;
    /** Status change actions triggered by user usually have priority */
    isUIAction?: boolean;
    updateActiveAgentTimestamp?: boolean;
  }) => {
    const { status, isUIAction, updateActiveAgentTimestamp = true } = args;
    const isUpdateForActiveAgent = store.agent && store.agent.id === status.agentId;

    const agent = isUpdateForActiveAgent
      ? store.agent
      : store.agentsById[status.agentId];

    if (!agent) {
      return;
    }

    if (isUpdateForActiveAgent) {
      /* Ignore status updates while re-initializing agent status */
      const isReasonExpire = status.statusChangeContext?.reason === STATUS_CHANGE_REASON.EXPIRE;
      if ((!isUIAction && store.isStatusDesync) || isReasonExpire) {
        return;
      }

      /* Ignore outdated status updates for current agent */
      const timestamp = new Date(status.lastStatusUpdate || 0).getTime();
      if (!isUIAction && store.statusUpdateTimestamp > timestamp) {
        return;
      }

      /* Auto-update timestamp based on server-provided timestamp */
      if (updateActiveAgentTimestamp) {
        store.statusUpdateTimestamp = timestamp;
      }
    }

    set(agent, 'statusData', status);
  };

  /* We should ignore updates to current agent, that are not explicitly requested */
  const addAgentsToList = (agentList: IAgent[]) => {
    const patchedAgentList = agentList.map(agent => ({
      ...agent,
      contactRuleGroupIds: agent.contactRuleGroupIds ?? [],
    }));
    const agentsById = _.keyBy(patchedAgentList, 'id');
    if (store.agent) {
      delete agentsById[store.agent.id];
    }

    store.agentsById = { ...store.agentsById, ...agentsById };
  };

  const searchInAgents = (
    search: string,
    ruleGroupsById: Record<string, RuleGroup> = {},
    contactRuleGroupIds: Record<string, RuleGroup> = {},
    includeActiveAgent: boolean = false,
  ) => {
    const searchTerm = search.trim().toLowerCase();
    const agents = includeActiveAgent && store.agent
      ? [store.agent].concat(store.agents)
      : store.agents;

    if (!searchTerm) {
      return agents;
    }

    const SEARCH_KEYS = [
      'email',
      'username',
      'data.firstName',
      'data.lastName',
      'statusData.status',
      'statusData.label',
    ];
    return agents.filter(agent => {
      const isMatchingAgentByMeta = SEARCH_KEYS.some(key => {
        const value = _.get(agent, key, '').toLowerCase();
        return value.includes(searchTerm);
      });

      const isMatchingAgentByRuleGroups = agent.ruleGroupIds.some(id => {
        const ruleGroup = ruleGroupsById[id];
        if (ruleGroup) {
          return ruleGroup.name.toLowerCase().includes(searchTerm);
        }

        return false;
      });

      const isMatchingAgentByContactRuleGroups = agent.contactRuleGroupIds.some(id => {
        const contactRuleGroup = contactRuleGroupIds[id];
        if (contactRuleGroup) {
          return contactRuleGroup.name.toLowerCase().includes(searchTerm);
        }

        return false;
      });

      return isMatchingAgentByMeta || isMatchingAgentByRuleGroups || isMatchingAgentByContactRuleGroups;
    });
  };

  /** TODO: Remove copy-paste */
  const updateAgentInStore = (args: {
    agentId: string;
    ruleGroupIds?: string[];
    contactRuleGroupIds?: string[];
    meta?: object;
  }) => {
    const { agentId, ruleGroupIds, contactRuleGroupIds, meta } = args;

    if (store.agent && store.agent.id === agentId) {
      if (ruleGroupIds) {
        store.agent = { ...store.agent, ruleGroupIds };
      }
      if (contactRuleGroupIds) {
        store.agent = { ...store.agent, contactRuleGroupIds };
      }
      if (meta) {
        store.agent = { ...store.agent, meta };
      }

      return;
    }

    if (ruleGroupIds) {
      store.agentsById[agentId] = { ...store.agentsById[agentId], ruleGroupIds };
    }
    if (contactRuleGroupIds) {
      store.agentsById[agentId] = { ...store.agentsById[agentId], contactRuleGroupIds };
    }
    if (meta) {
      store.agentsById[agentId] = { ...store.agentsById[agentId], meta };
    }
  };

  const setIsStatusDesync = (isDesync: boolean) => {
    store.isStatusDesync = isDesync;
  };

  return {
    setActiveAgent,
    setAgentStatus,
    addAgentsToList,
    searchInAgents,
    updateAgentInStore,
    setIsStatusDesync,
  };
};
